import {
  AttachedDocumentsTabFields,
  ConsignmentTabFields,
  HeaderTabFields,
  ItemsTabFields,
  NamesAndPartiesTabFields,
} from '../types/fieldEnums';
import {
  type IAdditionalFieldConfig,
  type IAttachedDocumentResponse,
  type IItemResponse,
  type ILpcoResponse,
} from '../types/response';
import { type IAdditionalField, type IDBOptionAdditionalField } from '../types/request';
import { FieldPrefix } from '../constants/fieldPrefixes';
import { type IConfig } from '../types/configs';

import { checkIsValidAdditionalFieldValue } from './importValidations';
import { generateUnionOfEditableFields } from './fieldConfigParser';
import { replaceSpecialCharacters } from './importExportFieldName';
import { getAdditionalFieldsConfigMap } from './getFormValues';

function convertAdditionalFieldsData({
  additionalFieldConfig,
  additionalFields,
  editableFields,
  prefix = '',
}: {
  additionalFieldConfig: IAdditionalFieldConfig[];
  additionalFields: IAdditionalField[];
  editableFields: Set<string>;
  prefix?: string;
}) {
  const additionalFieldsConfigMap = getAdditionalFieldsConfigMap(additionalFieldConfig);

  const normalizedAdditionalFields = additionalFields.map((field) => ({
    ...field,
    fieldValue: Array.isArray(field.fieldValue) ? field.fieldValue.join(':') : field.fieldValue,
    fieldId: additionalFieldConfig.find((a) => replaceSpecialCharacters(a.englishLabel) === field.fieldName)?.id,
  }));

  return normalizedAdditionalFields.map((additionalField) => {
    const { fieldId = '' } = additionalField;

    if (editableFields.has(`${prefix}${FieldPrefix.additionalFields}${FieldPrefix.additionalField}${fieldId}`)) {
      checkIsValidAdditionalFieldValue(
        additionalField.fieldValue,
        additionalFieldsConfigMap[`${FieldPrefix.additionalField}${fieldId}`]
      );

      return additionalField;
    }

    return { fieldId, fieldValue: null };
  });
}

function convertDbOptionAdditionalFieldsData({
  dbOptionAdditionalFields,
  editableFields,
  additionalFieldConfig,
  prefix = '',
}: {
  dbOptionAdditionalFields: IDBOptionAdditionalField[];
  editableFields: Set<string>;
  additionalFieldConfig: IAdditionalFieldConfig[];
  prefix?: string;
}) {
  const normalizedAdditionalFields = dbOptionAdditionalFields.map((field) => {
    const config = additionalFieldConfig.find((a) => replaceSpecialCharacters(a.englishLabel) === field.fieldName);

    return {
      ...field,
      fieldId: config?.id,
    };
  });

  return normalizedAdditionalFields.map((dbOptionAdditionalField) => {
    const { fieldId = '' } = dbOptionAdditionalField;

    if (editableFields.has(`${prefix}${FieldPrefix.additionalFields}${FieldPrefix.additionalField}${fieldId}`)) {
      return dbOptionAdditionalField;
    }

    return { fieldId, optionId: null, fieldCode: null, fieldValue1: null, fieldValue2: null };
  });
}

function convertItemsData({
  items,
  editableFields,
  additionalFieldConfig,
}: {
  items: IItemResponse[];
  editableFields: Set<string>;
  additionalFieldConfig: IAdditionalFieldConfig[];
}) {
  return items.map((item) => {
    const {
      commodityDescription,
      countryOfOriginName,
      packageTypeName,
      requestedUomName,
      requestedUomName2,
      additionalFields,
      dbOptionAdditionalFields,
      packages,
      treatments,
      ...itemFields
    } = item;
    const finalData: Record<string, unknown> = {
      commodityDescription: editableFields.has(`${FieldPrefix.item}${ItemsTabFields.commodityCode}`)
        ? commodityDescription
        : null,
      countryOfOriginName: editableFields.has(`${FieldPrefix.item}${ItemsTabFields.countryOfOriginCode}`)
        ? countryOfOriginName
        : null,
      packageTypeName: editableFields.has(`${FieldPrefix.item}${ItemsTabFields.packageTypeCode}`)
        ? packageTypeName
        : null,
      requestedUomName: editableFields.has(`${FieldPrefix.item}${ItemsTabFields.requestedUomCode}`)
        ? requestedUomName
        : null,
      requestedUomName2: editableFields.has(`${FieldPrefix.item}${ItemsTabFields.requestedUomCode2}`)
        ? requestedUomName2
        : null,
      additionalFields: additionalFields
        ? convertAdditionalFieldsData({
            additionalFields,
            editableFields,
            prefix: FieldPrefix.item,
            additionalFieldConfig,
          })
        : [],
      dbOptionAdditionalFields: dbOptionAdditionalFields
        ? convertDbOptionAdditionalFieldsData({
            additionalFieldConfig,
            dbOptionAdditionalFields,
            editableFields,
            prefix: FieldPrefix.item,
          })
        : [],
      packages,
      treatments,
    };

    Object.entries(itemFields).forEach(([key, value]) => {
      if (editableFields.has(`${FieldPrefix.item}${key}`)) {
        finalData[key] = value;
      }
    });

    return finalData;
  });
}

function convertAttachedDocumentsData({
  attachedDocuments,
  editableFields,
}: {
  attachedDocuments: IAttachedDocumentResponse[];
  editableFields: Set<string>;
}) {
  return attachedDocuments.map((attachedDocument) => {
    const { name, ...attachedDocumentFields } = attachedDocument;

    const finalData: Record<string, unknown> = {
      name: editableFields.has(`${FieldPrefix.attachedDocument}${AttachedDocumentsTabFields.code}`) ? name : null,
    };

    Object.entries(attachedDocumentFields).forEach(([key, value]) => {
      if (editableFields.has(`${FieldPrefix.attachedDocument}${key}`)) {
        finalData[key] = value;
      }
    });

    return finalData;
  });
}

function convertImportedData({
  importedObject,
  configs,
  additionalFieldConfig,
}: {
  importedObject: unknown;
  configs: IConfig;
  additionalFieldConfig: IAdditionalFieldConfig[];
}) {
  const editableFields = generateUnionOfEditableFields(configs.operations);

  const {
    items,
    attachedDocuments,
    fees,
    additionalFields,
    dbOptionAdditionalFields,
    beneficiaries,
    companyAddress,
    countryOfDestinationName,
    departmentOfficeName,
    customsOfficeName,
    termsOfDeliveryDescription,
    invoiceExchangeRate,
    placeOfLoadingName,
    placeOfUnloadingName,
    companyDescription,
    userReferenceNumber,
    version,
    transitCountryName,
    meanOfTransportName,
    ...lpcoFields
  } = importedObject as Partial<ILpcoResponse>;

  const finalData: Record<string, unknown> = {
    companyAddress,
    countryOfDestinationName: editableFields.has(HeaderTabFields.countryOfDestinationCode)
      ? countryOfDestinationName
      : null,
    departmentOfficeName: editableFields.has(HeaderTabFields.departmentOfficeCode) ? departmentOfficeName : null,
    customsOfficeName: editableFields.has(HeaderTabFields.customsOfficeCode) ? customsOfficeName : null,
    termsOfDeliveryDescription: editableFields.has(HeaderTabFields.termsOfDeliveryCode)
      ? termsOfDeliveryDescription
      : null,
    invoiceExchangeRate: editableFields.has(HeaderTabFields.invoiceCurrencyCode) ? invoiceExchangeRate : null,
    placeOfLoadingName: editableFields.has(HeaderTabFields.placeOfLoadingCode) ? placeOfLoadingName : null,
    placeOfUnloadingName: editableFields.has(HeaderTabFields.placeOfUnloadingName) ? placeOfUnloadingName : null,
    companyDescription: editableFields.has(NamesAndPartiesTabFields.companyCode) ? companyDescription : null,
    transitCountryName: editableFields.has(ConsignmentTabFields.transitCountryCode) ? transitCountryName : null,
    meanOfTransportName: editableFields.has(ConsignmentTabFields.meanOfTransportCode) ? meanOfTransportName : null,
    additionalFields: additionalFields
      ? convertAdditionalFieldsData({ additionalFields, editableFields, additionalFieldConfig })
      : [],
    dbOptionAdditionalFields: dbOptionAdditionalFields
      ? convertDbOptionAdditionalFieldsData({ dbOptionAdditionalFields, editableFields, additionalFieldConfig })
      : [],
    items: items ? convertItemsData({ items, editableFields, additionalFieldConfig }) : [],
    attachedDocuments: attachedDocuments ? convertAttachedDocumentsData({ attachedDocuments, editableFields }) : [],
    fees: [],
    beneficiaries: [],
    version: null,
  };

  Object.entries(lpcoFields).forEach(([key, value]) => {
    if (editableFields.has(key)) {
      finalData[key] = value;
    }
  });

  return finalData;
}

export default convertImportedData;
